import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { addActivity } from '../utils/activities';
import CustomerSelect from '../components/CustomerSelect';
import {
  DEFAULT_LOAN_STATUS,
  formatLoanDisplayId,
  formatLoanCreatedAt,
  getLoanFollowUpDate,
  getLoanVisibleFollowUpDate,
  formatTenureYears,
  LOAN_STATUS_FLOW,
  getLoanStatusBySlug,
} from '../utils/loanWorkflow';
import { addToRecycleBin } from '../utils/recycleBin';
import { restoreDeletedEntry } from '../utils/recycleBinApi';
import {
  createLoanRecord,
  deleteLoanRecord,
  fetchLoanRecord,
  normalizeLoanApiRecord,
  readCachedCustomers,
  readCachedLoans,
  syncLoansCache,
  updateLoanRecord,
  writeCachedLoans,
} from '../utils/crmData';
import './Loans.css';

// Utility function to format amount with Indian numbering system
function formatAmountInput(value) {
  if (!value) return '';
  
  // Remove all non-digit characters
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (!numericValue) return '';
  
  // Format with Indian numbering system (commas)
  return Number(numericValue).toLocaleString('en-IN');
}

// Utility function to parse formatted amount back to number
function parseAmountInput(formattedValue) {
  return formattedValue.replace(/,/g, '');
}

const emptyLoanForm = {
  loanId: '',
  customerId: '',
  customer: '',
  phone: '',
  lenderName: '',
  bankerName: '',
  referenceName: '',
  amount: '',
  type: 'Personal',
  interest: '',
  tenure: '',
  status: DEFAULT_LOAN_STATUS,
  followUpDate: '',
  remark: '',
};

const DEFAULT_LOAN_TYPES = [
  'Personal',
  'Home',
  'Vehicle',
  'Business',
  'Education',
  'Loan Agent Property',
  'Balance Transfer',
  'Non-Residential Property',
  'BT and Top-Up',
  'Flat Purchase',
  'Shop Purchase',
  'Rental',
];

function recordActivitySafely(payload) {
  try {
    addActivity(payload);
  } catch (error) {
    // Ignore activity failures to keep loan actions responsive.
  }
}

function Loans() {
  const { statusSlug } = useParams();
  const [loans, setLoans] = useState(() => readCachedLoans());
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [pageError, setPageError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loanTypeFilter, setLoanTypeFilter] = useState('all');
  const [followUpFrom, setFollowUpFrom] = useState('');
  const [followUpTo, setFollowUpTo] = useState('');
  const initialStatusFromSlug = getLoanStatusBySlug(statusSlug) || 'all';
  const [statusFilter, setStatusFilter] = useState(() => (
    initialStatusFromSlug === 'all' ? ['all'] : [initialStatusFromSlug]
  ));
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const statusButtonRef = useRef(null);
  const [statusMenuPosition, setStatusMenuPosition] = useState({ top: 0, left: 0, width: 220 });
  const [showModal, setShowModal] = useState(false);
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [newLoan, setNewLoan] = useState(emptyLoanForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const { user } = useAuth();
  const canDelete = user?.role === 'superuser';

  const toastTimerRef = useRef(null);
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    loanId: null,
    prevStatus: null,
    action: null,
    entry: null,
  });

  useEffect(() => {
    const s = getLoanStatusBySlug(statusSlug) || 'all';
    setStatusFilter(s === 'all' ? ['all'] : [s]);
  }, [statusSlug]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // compute dropdown position to use fixed positioning (escape stacking contexts)
  const openStatusDropdown = () => {
    const btn = statusButtonRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setStatusMenuPosition({ top: rect.bottom + 8, left: rect.left, width: Math.max(220, rect.width) });
    }
    setStatusDropdownOpen(true);
  };

  useEffect(() => {
    let isMounted = true;

    const loadLoans = async () => {
      try {
        setLoadingLoans(true);
        setPageError('');
        const nextLoans = await syncLoansCache();
        if (isMounted) {
          setLoans(nextLoans);
        }
      } catch (error) {
        if (isMounted) {
          setLoans(readCachedLoans());
          setPageError(error.message || 'Failed to load loans from the server.');
        }
      } finally {
        if (isMounted) {
          setLoadingLoans(false);
        }
      }
    };

    loadLoans();

    return () => {
      isMounted = false;
    };
  }, []);

  const clearToast = () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast({ visible: false, message: '', loanId: null, prevStatus: null, action: null, entry: null });
  };

  const showToast = (message, loanId, prevStatus = null, action = 'status', entry = null) => {
    clearToast();
    setToast({ visible: true, message, loanId, prevStatus, action, entry });
    toastTimerRef.current = setTimeout(() => {
      clearToast();
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // recompute dropdown position on resize/scroll when open
  useEffect(() => {
    if (!statusDropdownOpen) return undefined;
    const onResize = () => {
      const btn = statusButtonRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setStatusMenuPosition({ top: rect.bottom + 8, left: rect.left, width: Math.max(220, rect.width) });
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [statusDropdownOpen]);

  const formatDateInputValue = (value) => {
    if (!value) {
      return '';
    }

    const trimmedValue = String(value).trim();
    const leadingDateMatch = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (leadingDateMatch) {
      return leadingDateMatch[1];
    }

    const parsed = new Date(trimmedValue);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
  };

  const formatDateValue = (value) => {
    if (!value) {
      return '-';
    }

    const normalizedDate = formatDateInputValue(value);
    if (normalizedDate) {
      return new Date(`${normalizedDate}T00:00:00`).toLocaleDateString();
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
  };

  const filteredLoans = loans.filter((loan) => {
    const searchValue = searchTerm.toLowerCase();
    const visibleFollowUpDate = getLoanVisibleFollowUpDate(loan);
    const formattedFollowUpDate = formatDateValue(visibleFollowUpDate);
    const matchesSearch = [
      loan.loanId,
      loan.id,
      loan.customer,
      loan.lenderName,
      loan.referenceName,
      loan.date,
      visibleFollowUpDate,
      formattedFollowUpDate,
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(searchValue));
    const matchesStatus = statusFilter.includes('all') || statusFilter.length === 0 || statusFilter.includes(loan.status);
    const matchesLoanType = loanTypeFilter === 'all' || loan.type === loanTypeFilter;
    const followUpDate = getLoanFollowUpDate({ ...loan, followUpDate: visibleFollowUpDate });
    const fromDate = followUpFrom ? new Date(`${followUpFrom}T00:00:00`) : null;
    const toDate = followUpTo ? new Date(`${followUpTo}T23:59:59.999`) : null;
    const matchesFrom = !fromDate || (followUpDate && followUpDate >= fromDate);
    const matchesTo = !toDate || (followUpDate && followUpDate <= toDate);
    return matchesSearch && matchesStatus && matchesLoanType && matchesFrom && matchesTo;
  });
  const filteredLoanCountLabel = `${filteredLoans.length} ${filteredLoans.length === 1 ? 'loan / entry' : 'loans / entries'}`;
  const filteredLoanTotalAmount = filteredLoans.reduce((sum, loan) => sum + Number(loan.amount || 0), 0);
  const loanTypeOptions = Array.from(new Set([
    ...DEFAULT_LOAN_TYPES,
    ...loans.map((loan) => loan.type).filter(Boolean),
  ]));

  const closeModal = () => {
    setShowModal(false);
    setEditingLoanId(null);
    setNewLoan(emptyLoanForm);
    setPendingStatusUpdate(null);
    setFormError('');
    setSaving(false);
  };

  const openCreateModal = () => {
    setEditingLoanId(null);
    setNewLoan(emptyLoanForm);
    setPendingStatusUpdate(null);
    setFormError('');
    setShowModal(true);
  };

  const persistLoans = (nextLoans) => {
    const normalizedLoans = (Array.isArray(nextLoans) ? nextLoans : []).map(normalizeLoanApiRecord);
    setLoans(normalizedLoans);
    writeCachedLoans(normalizedLoans);
  };

  const handleSaveLoan = async (e) => {
    e.preventDefault();
    setSaving(true);
    setPageError('');
    setFormError('');

    const matchedCustomer = newLoan.customerId
      ? null
      : readCachedCustomers().find(
          (customer) => (customer.name || '').trim().toLowerCase() === newLoan.customer.trim().toLowerCase()
        );
    const customerId = newLoan.customerId || (matchedCustomer ? String(matchedCustomer.id) : '');
    const customerName = matchedCustomer?.name || newLoan.customer;

    const normalizedStatus = String(newLoan.status || '').trim();
    const isLoginDoneStatus = normalizedStatus.toLowerCase() === 'login done';
    const normalizedFollowUpDate = formatDateInputValue(newLoan.followUpDate);

    if (isLoginDoneStatus && !newLoan.loanId.trim()) {
      setFormError('Loan ID is required when status is Login Done.');
      setSaving(false);
      return;
    }

    if (pendingStatusUpdate && !normalizedFollowUpDate) {
      setFormError('Follow-up date is required when changing loan status.');
      setSaving(false);
      return;
    }

    if (!isLoginDoneStatus && !newLoan.loanId.trim()) {
      // Allow loans to be created/edited without manual ID until Login Done.
      // keep loanId blank in the payload to preserve workflow.
    }

    if (!customerId) {
      setFormError('Select an existing customer from the customer suggestions before creating the loan.');
      setSaving(false);
      return;
    }

    const payload = {
      loanId: newLoan.loanId.trim(),
      customerId,
      customer: customerName,
      phone: newLoan.phone.trim(),
      lenderName: newLoan.lenderName,
      bankerName: newLoan.bankerName,
      referenceName: newLoan.referenceName,
      amount: Number(newLoan.amount) || 0,
      type: newLoan.type,
      interest: Number(newLoan.interest) || 0,
      tenure: Number(newLoan.tenure) || 0,
      status: newLoan.status || DEFAULT_LOAN_STATUS,
      followUpDate: normalizedFollowUpDate,
      remark: newLoan.remark || '',
    };

    try {
      let nextLoans = loans;

      if (editingLoanId) {
        const updatedLoan = normalizeLoanApiRecord({
          ...loans.find((loan) => loan.id === editingLoanId),
          ...(await updateLoanRecord(editingLoanId, payload)),
        });
        nextLoans = loans.map((loan) => (
          loan.id === editingLoanId ? updatedLoan : loan
        ));
      } else {
        const createdLoan = await createLoanRecord(payload);
        nextLoans = [createdLoan, ...loans];

        recordActivitySafely({
          type: 'loan_created',
          actor: user?.username || 'system',
          message: `Loan ${formatLoanDisplayId(createdLoan)} created for ${createdLoan.customer}`,
          loanId: createdLoan.id,
          customerId: createdLoan.customerId,
          customerName: createdLoan.customer,
          meta: { loanId: createdLoan.id, customerId: createdLoan.customerId, customerName: createdLoan.customer },
        });
      }

      persistLoans(nextLoans);

      if (pendingStatusUpdate) {
        const updatedStatusLoan = nextLoans.find((item) => item.id === editingLoanId);

        showToast(
          `Loan ${formatLoanDisplayId(updatedStatusLoan)} moved to ${pendingStatusUpdate.nextStatus}`,
          editingLoanId,
          pendingStatusUpdate.previousStatus
        );

      }

      closeModal();
    } catch (error) {
      setFormError(error.message || 'Failed to save loan.');
      setSaving(false);
    }
  };

  const handleEditClick = (loan) => {
    setEditingLoanId(loan.id);
    setPendingStatusUpdate(null);
    setFormError('');

    // Fetch customer details to get referenceName
    const customer = readCachedCustomers().find((c) => String(c.id) === String(loan.customerId));

    setNewLoan({
      loanId: loan.loanId || '',
      customerId: loan.customerId || '',
      customer: loan.customer || '',
      phone: loan.phone || customer?.phone || '',
      lenderName: loan.lenderName || '',
      bankerName: loan.bankerName || '',
      referenceName: customer?.referenceName || loan.referenceName || '',
      amount: loan.amount != null ? String(loan.amount) : '',
      type: loan.type || 'Personal',
      interest: loan.interest != null ? String(loan.interest) : '',
      tenure: loan.tenure != null ? String(loan.tenure) : '',
      status: loan.status || DEFAULT_LOAN_STATUS,
      followUpDate: formatDateInputValue(getLoanVisibleFollowUpDate(loan)),
      remark: loan.remark || '',
    });
    setShowModal(true);
  };

  const handleStatusChange = async (loan, nextStatus) => {
    const previousStatus = loan.status || DEFAULT_LOAN_STATUS;
    if (previousStatus === nextStatus) {
      return;
    }

    setEditingLoanId(loan.id);
    setFormError('');
    setPendingStatusUpdate({ loanId: loan.id, previousStatus, nextStatus });

    // Fetch fresh loan data from API so the modal shows the latest values.
    try {
      const freshLoan = await fetchLoanRecord(loan.id);
      setNewLoan({
        loanId: freshLoan.loanId || '',
        customerId: freshLoan.customerId || '',
        customer: freshLoan.customer || '',
        phone: freshLoan.phone || '',
        lenderName: freshLoan.lenderName || '',
        bankerName: freshLoan.bankerName || '',
        referenceName: freshLoan.referenceName || '',
        amount: freshLoan.amount != null ? String(freshLoan.amount) : '',
        type: freshLoan.type || 'Personal',
        interest: freshLoan.interest != null ? String(freshLoan.interest) : '',
        tenure: freshLoan.tenure != null ? String(freshLoan.tenure) : '',
        status: nextStatus,
        followUpDate: formatDateInputValue(freshLoan.followUpDate),
        remark: freshLoan.remark || '',
      });
    } catch (error) {
      setNewLoan({
        loanId: loan.loanId || '',
        customerId: loan.customerId || '',
        customer: loan.customer || '',
        phone: loan.phone || '',
        lenderName: loan.lenderName || '',
        bankerName: loan.bankerName || '',
        referenceName: loan.referenceName || '',
        amount: loan.amount != null ? String(loan.amount) : '',
        type: loan.type || 'Personal',
        interest: loan.interest != null ? String(loan.interest) : '',
        tenure: loan.tenure != null ? String(loan.tenure) : '',
        status: nextStatus,
        followUpDate: formatDateInputValue(getLoanVisibleFollowUpDate(loan)),
        remark: loan.remark || '',
      });
    }

    setShowModal(true);
  };

  const undoStatusChange = async () => {
    if (!toast.loanId) {
      return;
    }

    if (toast.action === 'delete' && toast.entry) {
      try {
        const restoredLoan = await restoreDeletedEntry(toast.entry);
        persistLoans([restoredLoan, ...loans]);
        recordActivitySafely({
          type: 'loan_deleted_undo',
          actor: user?.username || 'system',
          message: 'Loan restored',
          loanId: toast.loanId,
          customerId: restoredLoan.customerId,
          customerName: restoredLoan.customer,
          meta: { loanId: toast.loanId, customerId: restoredLoan.customerId, customerName: restoredLoan.customer },
        });
      } catch (error) {
        setPageError(error.message || 'Failed to restore loan.');
      } finally {
        clearToast();
      }
      return;
    }

    if (toast.action === 'status' && toast.prevStatus != null) {
      try {
        const revertedLoan = await updateLoanRecord(toast.loanId, { status: toast.prevStatus });
        persistLoans(loans.map((loan) => (
          loan.id === toast.loanId ? revertedLoan : loan
        )));

        recordActivitySafely({
          type: 'loan_status_undo',
          actor: user?.username || 'system',
          message: `Loan status reverted to ${toast.prevStatus}`,
          loanId: toast.loanId,
          customerId: revertedLoan.customerId,
          customerName: revertedLoan.customer,
          meta: {
            loanId: toast.loanId,
            status: toast.prevStatus,
            customerId: revertedLoan.customerId,
            customerName: revertedLoan.customer,
          },
        });
      } catch (error) {
        setPageError(error.message || 'Failed to revert loan status.');
      } finally {
        clearToast();
      }
    }
  };

  const handleDeleteLoan = async (loan) => {
    if (!canDelete) {
      setPageError('Only superusers can delete loans.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete Loan ${formatLoanDisplayId(loan)}?`)) {
      return;
    }

    try {
      await deleteLoanRecord(loan.id);
      const recycleEntry = addToRecycleBin({ entityType: 'loans', item: loan });
      persistLoans(loans.filter((item) => item.id !== loan.id));
      showToast(`Loan ${formatLoanDisplayId(loan)} moved to recycle bin`, loan.id, null, 'delete', recycleEntry);

      recordActivitySafely({
        type: 'loan_deleted',
        actor: user?.username || 'system',
        message: `Loan ${formatLoanDisplayId(loan)} moved to recycle bin`,
        loanId: loan.id,
        customerId: loan.customerId,
        customerName: loan.customer,
        meta: { loanId: loan.id, customerId: loan.customerId, customerName: loan.customer },
      });
    } catch (error) {
      setPageError(error.message || 'Failed to delete loan.');
    }
  };

  const toggleStatusSelection = (status) => {
    // selecting 'all' overrides other selections
    if (status === 'all') {
      setStatusFilter(['all']);
      return;
    }

    setStatusFilter((prev) => {
      const hadAll = prev.includes('all');
      let next = hadAll ? prev.filter((s) => s !== 'all') : [...prev];

      if (next.includes(status)) {
        next = next.filter((s) => s !== status);
      } else {
        next.push(status);
      }

      if (next.length === 0) {
        return ['all'];
      }

      return next;
    });
  };

  const statusLabel = () => {
    if (statusFilter.includes('all') || statusFilter.length === 0) return 'All Status';
    if (statusFilter.length === 1) return statusFilter[0];
    return `${statusFilter.length} selected`;
  };

  return (
    <div className="loans-page">
      <div className="page-header">
        <div>
          <h1>Loans Management</h1>
          {!statusFilter.includes('all') && statusFilter.length > 0 ? (
            <p style={{ margin: '6px 0 0', color: '#64748b' }}>
              Showing loans in {statusFilter.join(', ')}
            </p>
          ) : null}
        </div>
        <button className="btn-primary" onClick={openCreateModal}>
          + New Loan
        </button>
      </div>

      {pageError ? <div className="error-message">{pageError}</div> : null}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by loan ID, customer, lender, refference, or date..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <input
          type="date"
          value={followUpFrom}
          onChange={(e) => setFollowUpFrom(e.target.value)}
          className="filter-input"
          aria-label="Follow-up from"
        />
        <input
          type="date"
          value={followUpTo}
          onChange={(e) => setFollowUpTo(e.target.value)}
          className="filter-input"
          aria-label="Follow-up to"
        />
        <select
          value={loanTypeFilter}
          onChange={(e) => setLoanTypeFilter(e.target.value)}
          className="filter-select"
          aria-label="Filter by loan type"
        >
          <option value="all">All Types</option>
          {loanTypeOptions.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <div className="filter-select multi-status-dropdown" ref={statusDropdownRef} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            ref={statusButtonRef}
            className="filter-select"
            onClick={(e) => {
              e.stopPropagation();
              statusDropdownOpen ? setStatusDropdownOpen(false) : openStatusDropdown();
            }}
          >
            {statusLabel()}
          </button>
        </div>
        {statusDropdownOpen && createPortal(
          <div
            className="dropdown-menu"
            style={{ position: 'fixed', top: statusMenuPosition.top, left: statusMenuPosition.left, minWidth: statusMenuPosition.width, zIndex: 99999 }}
            onClick={(e) => e.stopPropagation()}
          >
            <label className="dropdown-item">
              <input
                type="checkbox"
                checked={statusFilter.includes('all')}
                onChange={() => toggleStatusSelection('all')}
              />
              <span>All Status</span>
            </label>
            <div className="dropdown-divider" />
            {LOAN_STATUS_FLOW.map((status) => (
              <label className="dropdown-item" key={status}>
                <input
                  type="checkbox"
                  checked={statusFilter.includes(status)}
                  onChange={() => toggleStatusSelection(status)}
                />
                <span>{status}</span>
              </label>
            ))}
          </div>,
          document.body
        )}
      </div>

      <div className="loans-table-container">
        <table className="loans-table">
          <thead>
            <tr>
              <th>Loan ID</th>
              <th>Customer</th>
              <th>Lender Name</th>
              <th>Refference Name</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Interest</th>
              <th>Tenure</th>
              <th>Status</th>
              <th>Follow-up Date</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingLoans ? (
              <tr>
                <td colSpan="12">Loading loans from the server...</td>
              </tr>
            ) : filteredLoans.map((loan) => (
              <tr key={loan.id}>
                <td>{formatLoanDisplayId(loan)}</td>
                <td>
                  {loan.customerId ? (
                    <Link to={`/customers/${loan.customerId}`} className="customer-link">
                      {loan.customer}
                    </Link>
                  ) : (
                    <span>{loan.customer}</span>
                  )}
                </td>
                <td>{loan.lenderName || '-'}</td>
                <td>{loan.referenceName || '-'}</td>
                <td>{loan.type}</td>
                <td>Rs. {loan.amount.toLocaleString('en-IN')}</td>
                <td>{loan.interest}%</td>
                <td>{formatTenureYears(loan.tenure)}</td>
                <td>
                  <select
                    className="status-select"
                    value={loan.status || DEFAULT_LOAN_STATUS}
                    onChange={(e) => handleStatusChange(loan, e.target.value)}
                  >
                    {LOAN_STATUS_FLOW.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </td>
                <td>{formatDateValue(getLoanVisibleFollowUpDate(loan))}</td>
                <td>{formatLoanCreatedAt(loan)}</td>
                <td>
                  <div className="action-buttons">
                    <Link to={`/loans/${loan.id}`} className="btn-view">View</Link>
                    <button className="btn-edit" onClick={() => handleEditClick(loan)}>Edit</button>
                    {canDelete ? (
                      <button className="btn-delete" onClick={() => handleDeleteLoan(loan)}>Delete</button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loadingLoans ? (
          <div className="loans-results-count" aria-live="polite">
            <span>{filteredLoanCountLabel}</span>
            <span>Total Amount: Rs. {filteredLoanTotalAmount.toLocaleString('en-IN')}</span>
          </div>
        ) : null}
      </div>

      {toast.visible ? (
        <div className="toast" role="status">
          <div className="toast-message">{toast.message}</div>
          <div className="toast-actions">
            <button type="button" className="btn-secondary" onClick={undoStatusChange}>Undo</button>
            <button type="button" className="btn-secondary" onClick={clearToast} style={{ marginLeft: 8 }}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {showModal ? (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{editingLoanId ? 'Edit Loan' : 'Create New Loan'}</h2>
                {newLoan.status === 'Login Done' && (
                  <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666' }}>
                    📌 Marking as "Login Done" - Customer is now prioritized. Fill in Manual Loan ID and other details.
                  </p>
                )}
                {pendingStatusUpdate && (
                  <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666' }}>
                    📌 Status change to "{pendingStatusUpdate.nextStatus}" requires a follow-up date before saving.
                  </p>
                )}
              </div>
              <button className="modal-close" onClick={closeModal}>x</button>
            </div>
            <form onSubmit={handleSaveLoan}>
              {formError ? <div className="modal-error-message">{formError}</div> : null}
              <div className="form-group">
                <label>Customer Name <span className="required-indicator">*</span></label>
                <CustomerSelect
                  mode="input"
                  valueId={newLoan.customerId}
                  valueName={newLoan.customer}
                  onChange={({ customerId, customerName, referenceName, phone }) => {
                    setFormError('');
                    setNewLoan({
                      ...newLoan,
                      customerId: customerId ? String(customerId) : '',
                      customer: customerName,
                      phone: phone || '',
                      referenceName: referenceName || '',
                    });
                  }}
                  placeholder="Search and select an existing customer"
                  required
                />
                <div className="field-hint">Choose the customer from the suggestions so the loan links to the correct record.</div>
              </div>

              <div className="form-group">
                <label>Customer Contact Number</label>
                <input
                  type="tel"
                  value={newLoan.phone}
                  onChange={(e) => setNewLoan({ ...newLoan, phone: e.target.value })}
                  placeholder="Enter customer contact number"
                />
                <div className="field-hint">Add or edit the contact number for this loan record.</div>
              </div>

              <div className="form-group">
                <label>
                  Follow-up Date {pendingStatusUpdate ? <span className="required-indicator">*</span> : null}
                </label>
                <input
                  type="date"
                  value={formatDateInputValue(newLoan.followUpDate)}
                  onChange={(e) => setNewLoan({ ...newLoan, followUpDate: e.target.value })}
                  required={Boolean(pendingStatusUpdate)}
                />
                <div className="field-hint">
                  Follow-up date is required when updating loan status from the loan list.
                </div>
              </div>

              <div className="form-group">
                <label>Manual Loan ID {String(newLoan.status || '').trim().toLowerCase() === 'login done' && <span className="required-indicator">*</span>}</label>
                <input
                  type="text"
                  value={newLoan.loanId}
                  onChange={(e) => {
                    setFormError('');
                    setNewLoan({ ...newLoan, loanId: e.target.value });
                  }}
                  placeholder="Enter manual loan ID"
                  disabled={String(newLoan.status || '').trim().toLowerCase() !== 'login done'}
                  required={String(newLoan.status || '').trim().toLowerCase() === 'login done'}
                />
                <div className="field-hint">
                  Manual Loan ID is editable only when loan status is "Login Done".
                  {String(newLoan.status || '').trim().toLowerCase() !== 'login done' ? ' Set status to Login Done to enter/update this field.' : ''}
                </div>
              </div>

              <div className="form-group">
                <label>System Record ID</label>
                <input
                  type="text"
                  value={editingLoanId || 'Will be created automatically after saving this loan'}
                  readOnly
                />
                <div className="field-hint">
                  This internal system ID is kept separately, even if the manual loan ID is edited later.
                </div>
              </div>

              <div className="form-group">
                <label>Lender Name (Bank Name) <span className="required-indicator">*</span></label>
                <input
                  type="text"
                  value={newLoan.lenderName}
                  onChange={(e) => setNewLoan({ ...newLoan, lenderName: e.target.value })}
                  placeholder="Type lender / bank name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Banker Name</label>
                <input
                  type="text"
                  value={newLoan.bankerName}
                  onChange={(e) => setNewLoan({ ...newLoan, bankerName: e.target.value })}
                  placeholder="Enter banker name"
                />
              </div>

              <div className="form-group">
                <label>Refference Name</label>
                <input
                  type="text"
                  value={newLoan.referenceName}
                  readOnly
                />
                <div className="field-hint">Reference name is automatically populated from the selected customer.</div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Loan Amount <span className="required-indicator">*</span></label>
                  <input
                    type="text"
                    value={formatAmountInput(newLoan.amount)}
                    onChange={(e) => {
                      const rawValue = parseAmountInput(e.target.value);
                      setNewLoan({ ...newLoan, amount: rawValue });
                    }}
                    placeholder="Enter amount (e.g., 50000)"
                    required
                  />
                  <div className="field-hint">Enter amount in rupees (commas added automatically)</div>
                </div>
                <div className="form-group">
                  <label>Loan Type</label>
                  <select
                    value={newLoan.type}
                    onChange={(e) => setNewLoan({ ...newLoan, type: e.target.value })}
                  >
                    {DEFAULT_LOAN_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Interest Rate (%) <span className="required-indicator">*</span></label>
                  <input
                    type="number"
                    step="0.1"
                    value={newLoan.interest}
                    onChange={(e) => setNewLoan({ ...newLoan, interest: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tenure (years) <span className="required-indicator">*</span></label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={newLoan.tenure}
                    onChange={(e) => setNewLoan({ ...newLoan, tenure: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={newLoan.status || DEFAULT_LOAN_STATUS}
                  onChange={(e) => setNewLoan({ ...newLoan, status: e.target.value })}
                >
                  {LOAN_STATUS_FLOW.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Remark</label>
                <textarea
                  value={newLoan.remark}
                  onChange={(e) => setNewLoan({ ...newLoan, remark: e.target.value })}
                  placeholder="Add remark or note for this loan"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingLoanId ? 'Save Changes' : 'Create Loan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Loans;
